import { supabase } from './supabaseClient';

/**
 * Data access for the personal watch list.
 *
 * Kept separate from api.ts, which serves the static news JSON over axios --
 * different backend, different concerns. Every Supabase call for watches lives
 * here so components never touch supabase.from() directly; swapping backends
 * later is then this one file.
 */

export interface Watch {
  id: string;
  user_id: string;
  brand: string;
  model: string;
  reference: string | null;

  // Specs. All nullable -- a watch is useful with none of them set. Text
  // rather than numbers because the units are part of the value ("39mm",
  // "70 hours"); year is the one real number.
  case_material: string | null;
  case_diameter: string | null;
  movement: string | null;
  power_reserve: string | null;
  water_resistance: string | null;
  crystal: string | null;
  strap: string | null;
  price_msrp: string | null;
  year: number | null;

  /**
   * 'seed'    = bundled reference data
   * 'manual'  = entered by the owner
   * 'scraped' = read off source_url by the scrape-watch function
   */
  specs_source: 'seed' | 'manual' | 'scraped' | null;
  specs_updated_at: string | null;

  /** Featured image. Written in a later phase; read-only for now. */
  image_url: string | null;

  /**
   * A page describing this model, supplied by the owner. Saving one triggers
   * a scrape (see supabase/functions/scrape-watch); it is kept afterwards so
   * the fetch can be repeated and so a scraped spec has a citable origin.
   */
  source_url: string | null;
  notes: string | null;
  created_at: string;
}

export type NewWatch = {
  brand: string;
  model: string;
  reference?: string | null;
  notes?: string | null;
  /** Supplying one at add time scrapes the page as soon as the row exists. */
  source_url?: string | null;
};

/** The spec columns, all optional -- patches touch only what changed. */
export type SpecPatch = Partial<
  Pick<
    Watch,
    | 'case_material'
    | 'case_diameter'
    | 'movement'
    | 'power_reserve'
    | 'water_resistance'
    | 'crystal'
    | 'strap'
    | 'price_msrp'
    | 'year'
    | 'specs_source'
    | 'specs_updated_at'
    | 'image_url'
  >
>;

export type WatchPatch = Partial<NewWatch> & SpecPatch & Partial<Pick<Watch, 'source_url'>>;

/** Spec columns that are free text, in the order they read best on screen. */
export const SPEC_TEXT_FIELDS = [
  'case_diameter',
  'case_material',
  'movement',
  'power_reserve',
  'water_resistance',
  'crystal',
  'strap',
  'price_msrp',
] as const;

export const SPEC_LABELS: Record<string, string> = {
  case_diameter: 'Case diameter',
  case_material: 'Case material',
  movement: 'Movement',
  power_reserve: 'Power reserve',
  water_resistance: 'Water resistance',
  crystal: 'Crystal',
  strap: 'Strap / bracelet',
  price_msrp: 'MSRP',
  year: 'Year',
};

// Blank inputs should land as NULL, not empty strings, so "no reference" has
// one representation in the database rather than two.
const orNull = (v: string | null | undefined) => {
  const trimmed = (v ?? '').trim();
  return trimmed === '' ? null : trimmed;
};

export const watchesApi = {
  /**
   * No user_id filter here on purpose: the RLS policy already restricts rows
   * to the signed-in user, so filtering again would only duplicate the rule in
   * a second place where it could drift.
   */
  list: async (): Promise<Watch[]> => {
    const { data, error } = await supabase
      .from('watches')
      .select('*')
      .order('brand')
      .order('model');
    if (error) throw error;
    return data ?? [];
  },

  /**
   * One watch by id, or null when it does not exist. RLS makes "someone
   * else's watch" indistinguishable from "no such row", which is what we
   * want -- the detail page treats both as not found rather than leaking
   * that an id is real.
   */
  get: async (id: string): Promise<Watch | null> => {
    const { data, error } = await supabase
      .from('watches')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ?? null;
  },

  add: async (watch: NewWatch): Promise<Watch> => {
    // user_id is set explicitly rather than by a database default of
    // auth.uid(). A default would work, but it would bake Supabase's auth
    // schema into the table -- exactly what keeping `watches` pointed at
    // `profiles` was meant to avoid.
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) throw new Error('You are not signed in.');

    const { data, error } = await supabase
      .from('watches')
      .insert({
        user_id: userId,
        brand: watch.brand.trim(),
        model: watch.model.trim(),
        reference: orNull(watch.reference),
        notes: orNull(watch.notes),
        source_url: orNull(watch.source_url),
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  update: async (id: string, patch: WatchPatch): Promise<Watch> => {
    const fields: Record<string, string | null> = {};
    const numericFields: Record<string, number | null> = {};
    if (patch.brand !== undefined) fields.brand = patch.brand.trim();
    if (patch.model !== undefined) fields.model = patch.model.trim();
    if (patch.reference !== undefined) fields.reference = orNull(patch.reference);
    if (patch.notes !== undefined) fields.notes = orNull(patch.notes);

    // Spec columns. Same blank-to-NULL rule as the rest, so clearing a field
    // in the form removes it rather than storing an empty string.
    for (const key of SPEC_TEXT_FIELDS) {
      if (patch[key] !== undefined) fields[key] = orNull(patch[key]);
    }
    if (patch.year !== undefined) {
      // Year arrives from a text input; an unparseable value clears it rather
      // than tripping the database's range constraint.
      const n = Number(patch.year);
      numericFields.year = patch.year === null || !Number.isFinite(n) ? null : Math.trunc(n);
    }
    if (patch.image_url !== undefined) fields.image_url = patch.image_url;
    if (patch.source_url !== undefined) fields.source_url = orNull(patch.source_url);
    if (patch.specs_source !== undefined) fields.specs_source = patch.specs_source;
    if (patch.specs_updated_at !== undefined) {
      fields.specs_updated_at = patch.specs_updated_at;
    }

    const { data, error } = await supabase
      .from('watches')
      .update({ ...fields, ...numericFields })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  remove: async (id: string): Promise<void> => {
    const { error } = await supabase.from('watches').delete().eq('id', id);
    if (error) throw error;
  },
};
